import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Plus, Trash2, Info, Tag, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Select } from '../../ui/newSelect';
import BrandSelect from '../../ui/BrandSelect';
import { adminToolbarSelect } from '../../utils/ReactSelectStyles';
import {
  BILLING_CURRENCY_OPTIONS,
  symbolToStripeCurrency,
  stripeCurrencyToSymbol,
  recurringToBillingFrequency,
  resolveBillingCurrencySymbol,
  formatOrganisationCurrency,
} from '../../utils/stripeCurrency';
import {
  findMatchingTier,
  resolveBillingUnit,
  isTieredPrice,
  formatMajorMoney,
} from '../../utils/stripeBanding';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription } from '../../ui/alert';
import Breadcrumb from '../../Common/Breadcrumb';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { Actions as ADMINACTIONS, getAllItemsFromDB } from '../../../store/actions/adminActions';
import { Actions as ORGANISATIONACTIONS } from '../../../store/actions/organisationActions';
import { Actions as LEGAL } from '../../../store/actions/legalActions';
import {
  buildAgreementCountryPayload,
  buildContractingEntityQueryParams,
  formatCyfContractingEntity,
} from '../../Modals/agreementsUtils';
import { toggleAlertMessage } from '../../../store/action';
import { contractToFormState, isEditableAgreementStatus } from './agreementFormUtils';

// `unitAmount` is in MAJOR units (e.g. `48` = £48.00). The picker exposes
// Stripe's pence-shaped `unit_amount`; we divide by 100 on pre-fill and submit.
// Tiered Prices leave `unitAmount` untouched and submit `bandingQuantity` instead.
const defaultLine = () => ({
  kind: 'subscription',
  billingFrequency: 'annual',
  unitAmount: '',
  quantity: 1,
  description: '',
  stripeProductId: '',
  stripePriceId: '',
  currency: '',
  discount: null,
  bandingQuantity: '',
});

// Backend canonicalises this; we mirror it so we can pre-empt `type_mismatch` 400s.
const canonicaliseAccountType = (raw) => {
  if (!raw) return '';
  if (raw === 'White Label Pending') return 'White Label';
  if (raw === 'Ambassador' || raw === 'White Label' || raw === 'Consultant' || raw === 'Direct') return raw;
  return '';
};

const ERROR_CODE_MESSAGES = {
  subscription_line_item_requires_stripe_price:
    'A subscription line item is missing its Stripe price. Pick one from the picker.',
  unsupported_currency:
    'That billing currency is not supported. Use £ / $ / € / AUD.',
  line_item_currency_mismatch:
    'A line item currency does not match the contract currency.',
  mixed_currency_line_items:
    'All subscription line items must share a single currency.',
  invalid_discount_type: 'Discount type must be percent or fixed.',
  invalid_discount_value:
    'Discount value is invalid (percent: 0–100; fixed: positive major units).',
  invalid_discount_duration: 'Discount duration must be forever, once, or repeating.',
  discount_duration_in_months_required:
    'Repeating discounts need a duration in months greater than 0.',
  discount_only_supported_on_subscriptions:
    'Discounts are only supported on subscription line items.',
  invalid_payMode: 'Pay mode is invalid.',
  invalid_lineItem_kind: 'Line item kind must be subscription or one_off.',
  invalid_term_type: 'Term type must be committed or rolling.',
  invalid_customerOrganisationId: 'The linked organisation id is invalid.',
  invalid_replaceExistingSubscription:
    'The replace-existing-subscription flag must be a boolean.',
  unknown_organisation:
    "We could not find that organisation. Pick a different one or switch to 'Create new'.",
  organisation_pending_agreement:
    'This organisation already has a pending agreement. Resend or void the existing invite first.',
  type_mismatch:
    "Type does not match the linked organisation's account type. Adjust the type to match.",
  no_published_legal_documents:
    'No legal documents are published yet. Publish at least one ToS / privacy version before creating agreements.',
  invalid_lineItem_bandingQuantity:
    'Banding quantity must be a positive whole number (e.g. annual revenue or FTE count).',
  tiered_line_item_requires_banding_quantity:
    'This price is tier-banded — enter the banding value (e.g. annual revenue or FTEs) before saving.',
  tiered_line_item_currency_mismatch:
    "The selected price's currency does not match the contract currency. Re-pick a Stripe price.",
  unknown_stripe_price:
    'We could not find that Stripe price anymore. Re-pick the product from the catalog.',
  jurisdiction_schedule_unavailable:
    'Published jurisdiction schedule is missing or invalid. Contact CYF admin.',
};

/** Shape `indexationPolicy` for POST /my/agreements — only fields relevant to each kind. */
const buildIndexationPolicy = (policy) => {
  if (!policy || policy.kind === 'none') return { kind: 'none' };
  const noticeDays = Number(policy.noticeDays) || 0;
  const floorPercent = Number(policy.floorPercent) || 0;
  if (policy.kind === 'fixed_percent') {
    return { kind: 'fixed_percent', floorPercent, noticeDays };
  }
  if (policy.kind === 'cpi_with_floor') {
    return {
      kind: 'cpi_with_floor',
      floorPercent,
      cpiSource: policy.cpiSource || 'UK_CPIH',
      noticeDays,
    };
  }
  return policy;
};

const friendlyErrorMessage = (result) => {
  if (!result) return 'Failed to create agreement';
  const raw = result?.error;
  const code = typeof raw === 'string' ? raw : raw?.code;
  if (code && ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code];
  return result?.message || 'Failed to create agreement';
};

const NewAgreement = ({
  createAgreement,
  voidAgreement,
  getAgreementContract,
  agreementsList,
  listOrganisationId,
  countries,
  showMessage,
  adminOrgs,
  whiteLabelAdminOrgs,
  productsByCurrency,
  productsLoading,
  currencies,
  getAdminOrganisations,
  getWLAdminOrgs,
  getStripeProducts,
  loadCurrencies,
  listCountries,
  getContractingEntity,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const isEdit = location.pathname.endsWith('/edit');
  const editId = isEdit ? routeId : null;
  const customerOrgId =
    new URLSearchParams(location.search).get('org') || listOrganisationId || '';
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(!!editId);
  const [editLoadError, setEditLoadError] = useState(null);
  // 'link' = pick an existing organisation; 'create' = create a brand-new one.
  const [customerMode, setCustomerMode] = useState('link');
  const [linkedOrgId, setLinkedOrgId] = useState('');
  const [replaceExistingSubscription, setReplaceExistingSubscription] = useState(false);
  const [billingCountryDiffers, setBillingCountryDiffers] = useState(false);
  const [contractingPreview, setContractingPreview] = useState(null);
  const [contractingLoading, setContractingLoading] = useState(false);
  const [contractingPreviewError, setContractingPreviewError] = useState(null);
  const [form, setForm] = useState({
    type: 'White Label',
    customerOrganisationName: '',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerRegisteredCountry: '',
    customerBillingCountry: '',
    billingCurrency: '£',
    whiteLabelId: '0',
    consultantId: 'NONE',
    endClientDefaultPlanType: 'per_report',
    perReportFeeVal: '',
    payMode: 'invoice',
    lineItems: [defaultLine()],
    term: {
      type: 'committed',
      lengthMonths: 36,
      startDate: '',
      noticePeriodDays: 60,
    },
    indexationPolicy: {
      kind: 'none',
      floorPercent: 5,
      cpiSource: 'UK_CPIH',
      noticeDays: 30,
    },
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateTerm = (k, v) => setForm((f) => ({ ...f, term: { ...f.term, [k]: v } }));
  const updateIdx = (k, v) => setForm((f) => ({ ...f, indexationPolicy: { ...f.indexationPolicy, [k]: v } }));

  useEffect(() => {
    if (!editId || !customerOrgId) {
      if (editId) setEditLoadError('Missing customer organisation. Open edit from the agreements list.');
      setEditLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      setEditLoadError(null);
      const contract = await getAgreementContract(editId, customerOrgId);
      if (cancelled) return;
      if (!contract) {
        setEditLoadError('Agreement not found for this organisation.');
        setEditLoading(false);
        return;
      }
      if (!isEditableAgreementStatus(contract.status)) {
        setEditLoadError(`Agreement status "${contract.status}" cannot be edited.`);
        setEditLoading(false);
        return;
      }
      const mapped = contractToFormState(contract);
      if (mapped) {
        setForm((f) => ({
          ...f,
          ...mapped,
          lineItems: mapped.lineItems || [defaultLine()],
        }));
        if (mapped.linkedExistingOrganisation && mapped.linkedOrganisationId) {
          setCustomerMode('link');
          setLinkedOrgId(String(mapped.linkedOrganisationId));
          setReplaceExistingSubscription(!!mapped.replaceExistingSubscription);
        } else {
          setCustomerMode('create');
        }
        if (
          mapped.customerBillingCountry &&
          mapped.customerRegisteredCountry &&
          mapped.customerBillingCountry !== mapped.customerRegisteredCountry
        ) {
          setBillingCountryDiffers(true);
        }
      }
      setEditLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, customerOrgId, getAgreementContract]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!adminOrgs || !adminOrgs.length) {
      getAdminOrganisations();
    }
    if (!whiteLabelAdminOrgs || !whiteLabelAdminOrgs.length) {
      getWLAdminOrgs();
    }
    if (!currencies || !currencies.length) {
      loadCurrencies();
    }
    if (!countries || !countries.length) {
      listCountries();
    }
  }, [
    adminOrgs,
    whiteLabelAdminOrgs,
    currencies,
    countries,
    getAdminOrganisations,
    getWLAdminOrgs,
    loadCurrencies,
    listCountries,
  ]);

  const effectiveBillingCountryId = billingCountryDiffers
    ? form.customerBillingCountry
    : form.customerRegisteredCountry;

  useEffect(() => {
    const inc = form.customerRegisteredCountry;
    const bill = effectiveBillingCountryId;
    if (!inc && !bill) {
      setContractingPreview(null);
      setContractingPreviewError(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setContractingLoading(true);
      setContractingPreviewError(null);
      const params = buildContractingEntityQueryParams({
        incorporationCountryId: inc,
        billingCountryId: bill,
        countries,
      });
      const result = await getContractingEntity(params);
      if (cancelled) return;
      setContractingLoading(false);
      if (result?.error) {
        setContractingPreview(null);
        setContractingPreviewError(
          result.error === 'no_entity_rows'
            ? 'Could not resolve a contracting entity from the jurisdiction schedule.'
            : result.error === 'not_authorised'
              ? 'Sign in required to preview the contracting entity.'
              : 'Could not load contracting entity preview.',
        );
        return;
      }
      setContractingPreview(result?.entity || null);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    form.customerRegisteredCountry,
    effectiveBillingCountryId,
    countries,
    getContractingEntity,
  ]);

  const contractIsoCurrency = useMemo(
    () => symbolToStripeCurrency(form.billingCurrency),
    [form.billingCurrency]
  );

  // Fetch the picker options whenever the contract currency resolves.
  useEffect(() => {
    if (!contractIsoCurrency) return;
    if (productsByCurrency && productsByCurrency[contractIsoCurrency]) return; // already cached
    getStripeProducts({ currency: contractIsoCurrency, limit: 50 });
    // We want this to run once per resolved currency, so we deliberately avoid
    // adding `productsByCurrency` to the dep array (which would re-run on every
    // dispatch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractIsoCurrency]);

  const productsForCurrency = useMemo(() => {
    if (!productsByCurrency) return [];
    if (contractIsoCurrency && productsByCurrency[contractIsoCurrency]) {
      return productsByCurrency[contractIsoCurrency];
    }
    return productsByCurrency._all || [];
  }, [productsByCurrency, contractIsoCurrency]);

  // Flatten products → one option per Price so admins pick the exact Price.
  // Tiered prices show the banding unit + tier count instead of a single
  // `unit_amount` (which is `null` on tiered Stripe Prices).
  const priceOptions = useMemo(() => {
    return (productsForCurrency || []).flatMap((p) =>
      (p.prices || []).map((price) => {
        const tiered = isTieredPrice(price);
        const recurring = price.recurring
          ? ` / ${price.recurring.interval_count || 1}${price.recurring.interval}`
          : '';
        const nickname = price.nickname ? ` · ${price.nickname}` : '';

        let amount;
        if (tiered) {
          const { label } = resolveBillingUnit(price, p);
          const tierCount = Array.isArray(price.tiers) ? price.tiers.length : 0;
          amount = `banded by ${label || 'tier'}${tierCount ? ` — ${tierCount} tiers` : ''}`;
        } else {
          amount =
            typeof price.unit_amount === 'number'
              ? `${stripeCurrencyToSymbol(price.currency)}${(price.unit_amount / 100).toFixed(2)}`
              : '—';
        }

        return {
          value: `${p.id}::${price.id}`,
          label: `${p.name} — ${amount}${recurring}${nickname}`,
          product: p,
          price,
        };
      })
    );
  }, [productsForCurrency]);

  const orgOptions = useMemo(
    () =>
      (adminOrgs || []).map((o) => ({
        value: o.id,
        label: o.name || o.id,
        org: o,
      })),
    [adminOrgs]
  );

  const orgValue = useMemo(
    () => orgOptions.find((opt) => opt.value === linkedOrgId) || null,
    [orgOptions, linkedOrgId]
  );

  const linkedOrg = orgValue?.org || null;
  const linkedAccountType = canonicaliseAccountType(linkedOrg?.accountType);
  const typeIsLocked = customerMode === 'link' && !!linkedAccountType;
  const linkedOrgIsConsultantParent =
    !!linkedOrg?.id &&
    !!linkedOrg?.consultantId &&
    String(linkedOrg.id) === String(linkedOrg.consultantId);

  // Convention used elsewhere in the admin (see ModalPropsAdminPortal.js):
  // a consultant "parent" org is one whose row's id equals its own consultantId.
  const consultantParentOptions = useMemo(
    () =>
      (adminOrgs || [])
        .filter((o) => o.id && o.consultantId && o.id === o.consultantId)
        .map((o) => ({ value: o.id, label: o.name || o.id })),
    [adminOrgs]
  );

  const wlParentOptions = useMemo(
    () =>
      (whiteLabelAdminOrgs || []).map((o) => ({
        value: o.whiteLabelId || o.id,
        label: o.name || o.whiteLabelId || o.id,
      })),
    [whiteLabelAdminOrgs]
  );

  // Reset the parent ids when the type changes so we don't carry stale values.
  useEffect(() => {
    setForm((f) => {
      const next = { ...f };
      if (f.type !== 'White Label' && f.whiteLabelId !== '0') next.whiteLabelId = '0';
      if (f.type !== 'Consultant' && f.consultantId !== 'NONE') next.consultantId = 'NONE';
      return next;
    });
  }, [form.type]);

  // Linked org IS the consultant — no separate parent picker; it references itself.
  useEffect(() => {
    if (form.type !== 'Consultant') return;
    if (customerMode === 'create') {
      setForm((f) => (f.consultantId === 'NONE' ? f : { ...f, consultantId: 'NONE' }));
      return;
    }
    if (linkedOrgIsConsultantParent && linkedOrgId) {
      setForm((f) => (f.consultantId === linkedOrgId ? f : { ...f, consultantId: linkedOrgId }));
    }
  }, [form.type, customerMode, linkedOrgIsConsultantParent, linkedOrgId]);

  // Lock the type to the linked org's account type to pre-empt `type_mismatch` 400s.
  useEffect(() => {
    if (typeIsLocked && form.type !== linkedAccountType) {
      update('type', linkedAccountType);
    }
  }, [typeIsLocked, linkedAccountType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBillingCurrencyChange = (symbol) => {
    setForm((f) => ({
      ...f,
      billingCurrency: symbol,
      lineItems: f.lineItems.map((row) => {
        if (row.kind !== 'subscription' || !row.stripePriceId) return row;
        return {
          ...row,
          stripeProductId: '',
          stripePriceId: '',
          currency: '',
          unitAmount: '',
          bandingQuantity: '',
        };
      }),
    }));
  };

  const onPickOrg = (opt) => {
    if (!opt) {
      setLinkedOrgId('');
      setReplaceExistingSubscription(false);
      return;
    }
    const org = opt.org || {};
    const suggestedBilling = resolveBillingCurrencySymbol(org.currency, currencies);
    setLinkedOrgId(opt.value);
    const orgCountryId = org.countryId || org.country || '';
    setForm((f) => ({
      ...f,
      customerOrganisationName: org.name || f.customerOrganisationName,
      customerRegisteredCountry: orgCountryId || f.customerRegisteredCountry,
      customerBillingCountry: '',
      billingCurrency: suggestedBilling || f.billingCurrency,
      consultantId:
        f.type === 'Consultant' && org.id && org.consultantId && String(org.id) === String(org.consultantId)
          ? org.id
          : f.consultantId,
    }));
    setBillingCountryDiffers(false);
  };

  const setLine = (i, k, v) => {
    setForm((f) => {
      const li = f.lineItems.map((row, j) => (j === i ? { ...row, [k]: v } : row));
      return { ...f, lineItems: li };
    });
  };

  const setLineDiscount = (i, patch) => {
    setForm((f) => {
      const li = f.lineItems.map((row, j) => {
        if (j !== i) return row;
        const next = patch === null ? null : { ...(row.discount || {}), ...patch };
        return { ...row, discount: next };
      });
      return { ...f, lineItems: li };
    });
  };

  const onPickPrice = (i, opt) => {
    if (!opt) {
      setForm((f) => {
        const li = f.lineItems.map((row, j) =>
          j === i
            ? {
                ...row,
                stripeProductId: '',
                stripePriceId: '',
                currency: row.currency || '',
                bandingQuantity: '',
              }
            : row
        );
        return { ...f, lineItems: li };
      });
      return;
    }
    const { product, price } = opt;
    const tiered = isTieredPrice(price);
    setForm((f) => {
      const li = f.lineItems.map((row, j) => {
        if (j !== i) return row;
        const next = {
          ...row,
          stripeProductId: product.id,
          stripePriceId: price.id,
          currency: price.currency,
        };
        // Per-unit Prices: convert pence → major units so the input + submit
        // both match the contract's major-unit convention. Tiered Prices
        // never carry a per-unit amount the admin needs to confirm — the
        // server snapshots the matched tier's flat_amount on create.
        if (tiered) {
          next.unitAmount = '';
          // Reset bandingQuantity when re-picking; admin enters the customer's
          // declared revenue / FTE next.
          next.bandingQuantity = '';
        } else {
          next.unitAmount =
            typeof price.unit_amount === 'number' ? price.unit_amount / 100 : '';
          next.bandingQuantity = '';
        }
        if (row.kind === 'subscription' && price.recurring) {
          const freq = recurringToBillingFrequency(price.recurring);
          if (freq) next.billingFrequency = freq;
        }
        if (!row.description) next.description = product.name;
        return next;
      });
      return { ...f, lineItems: li };
    });
  };

  const addLine = () => setForm((f) => ({ ...f, lineItems: [...f.lineItems, defaultLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  // -- pre-submit validation ---------------------------------------------------
  const validate = () => {
    if (customerMode === 'link' && !linkedOrgId) {
      return 'Pick an existing organisation or switch to "Create new"';
    }
    if (form.type === 'White Label' && (!form.whiteLabelId || form.whiteLabelId === '0')) {
      return 'Pick the white label partner this agreement belongs to';
    }
    if (form.type === 'Consultant' && customerMode === 'link' && !linkedOrgIsConsultantParent) {
      if (!form.consultantId || form.consultantId === 'NONE') {
        return 'Pick the consultant parent org, or link the consultant organisation itself (not an end client).';
      }
    }
    if (
      form.type === 'Consultant' &&
      form.endClientDefaultPlanType === 'per_report' &&
      (form.perReportFeeVal === '' || Number(form.perReportFeeVal) <= 0)
    ) {
      return 'Enter the default per-report fee for end clients (legacy billing model).';
    }
    if (!contractIsoCurrency) {
      return 'Billing currency must be £, $, €, or AUD.';
    }
    if (!form.customerRegisteredCountry) {
      if (customerMode === 'create') {
        return 'Select place of incorporation.';
      }
      return 'Linked organisation has no country on file — set place of incorporation before saving.';
    }
    if (billingCountryDiffers && !form.customerBillingCountry) {
      return 'Select billing country.';
    }

    const subCurrencies = new Set();
    for (let i = 0; i < form.lineItems.length; i++) {
      const row = form.lineItems[i];
      const idx = i + 1;
      if (row.kind === 'subscription') {
        if (!row.stripePriceId) {
          return `Pick a Stripe price for line ${idx} before saving.`;
        }
        if (!row.currency) {
          return `Line ${idx} is missing a currency.`;
        }
        if (row.currency !== contractIsoCurrency) {
          return `Line ${idx} currency (${row.currency}) does not match the contract currency (${contractIsoCurrency}).`;
        }
        subCurrencies.add(row.currency);

        const picked = priceOptions.find((o) => o.price?.id === row.stripePriceId);
        if (picked && isTieredPrice(picked.price)) {
          const q = Number(row.bandingQuantity);
          if (!Number.isFinite(q) || q <= 0 || !Number.isInteger(q)) {
            const { label } = resolveBillingUnit(picked.price, picked.product);
            return `Line ${idx} needs a positive whole-number ${label || 'banding quantity'} (this Stripe price is tier-banded).`;
          }
        }
      } else if (row.kind === 'one_off' && row.discount) {
        return `Discounts are only supported on subscription line items (line ${idx}).`;
      }
      if (row.discount) {
        const d = row.discount;
        if (d.type !== 'percent' && d.type !== 'fixed') {
          return `Line ${idx} discount type must be percent or fixed.`;
        }
        const value = Number(d.value);
        if (!Number.isFinite(value) || value <= 0) {
          return `Line ${idx} discount value must be greater than 0.`;
        }
        if (d.type === 'percent' && value > 100) {
          return `Line ${idx} percent discount must be ≤ 100.`;
        }
        if (d.duration !== 'forever' && d.duration !== 'once' && d.duration !== 'repeating') {
          return `Line ${idx} discount duration must be forever, once, or repeating.`;
        }
        if (d.duration === 'repeating' && !(Number(d.durationInMonths) > 0)) {
          return `Line ${idx} repeating discounts need a duration in months > 0.`;
        }
      }
    }
    if (subCurrencies.size > 1) {
      return 'All subscription line items must share a single currency.';
    }
    return null;
  };

  // -- submit ------------------------------------------------------------------
  const onSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      showMessage(true, err, 'error', 5000);
      return;
    }

    setSaving(true);

    const lineItems = form.lineItems.map((row) => {
      const base = {
        kind: row.kind,
        quantity: Number(row.quantity) || 1,
        description: row.description || undefined,
      };

      if (row.kind === 'subscription') {
        base.billingFrequency = row.billingFrequency;
        base.stripeProductId = row.stripeProductId;
        base.stripePriceId = row.stripePriceId;
        base.currency = row.currency;

        const picked = priceOptions.find((o) => o.price?.id === row.stripePriceId);
        const tiered = picked && isTieredPrice(picked.price);

        if (tiered) {
          // Tiered Prices: server snapshots flat_amount from the matched tier.
          // Send bandingQuantity; OMIT unitAmount per the agreements API contract.
          base.bandingQuantity = Number(row.bandingQuantity);
        } else {
          // Per-unit: unitAmount in MAJOR units (already stored that way in form state).
          base.unitAmount = Number(row.unitAmount) || 0;
        }

        if (row.discount) {
          const d = row.discount;
          const discount = {
            type: d.type,
            value: Number(d.value),
            duration: d.duration,
          };
          if (d.duration === 'repeating') {
            discount.durationInMonths = Number(d.durationInMonths);
          }
          base.discount = discount;
        }
      } else if (row.kind === 'one_off') {
        // One-off: unitAmount in major units; currency optional.
        base.unitAmount = Number(row.unitAmount) || 0;
        if (row.currency) base.currency = row.currency;
      }
      return base;
    });

    const body = {
      type: form.type,
      customerOrganisationName: form.customerOrganisationName,
      customerFirstName: form.customerFirstName,
      customerLastName: form.customerLastName,
      customerEmail: form.customerEmail,
      ...buildAgreementCountryPayload({
        incorporationCountryId: form.customerRegisteredCountry,
        billingCountryId: effectiveBillingCountryId,
        countries,
      }),
      billingCurrency: form.billingCurrency,
      whiteLabelId: form.whiteLabelId,
      consultantId: form.consultantId,
      payMode: form.payMode,
      lineItems,
      term: { ...form.term, lengthMonths: Number(form.term.lengthMonths) || 0, noticePeriodDays: Number(form.term.noticePeriodDays) || 0 },
      indexationPolicy: buildIndexationPolicy(form.indexationPolicy),
    };

    if (form.type === 'Consultant') {
      body.endClientDefaultPlanType = form.endClientDefaultPlanType;
      if (form.endClientDefaultPlanType === 'per_report' && form.perReportFeeVal !== '') {
        body.perReportFeeVal = Number(form.perReportFeeVal);
      }
    }

    if (customerMode === 'link' && linkedOrgId) {
      body.customerOrganisationId = linkedOrgId;
      body.replaceExistingSubscription = !!replaceExistingSubscription;
    }

    if (editId) {
      const voidR = await voidAgreement(
        editId,
        { reason: 'Superseded by revised order form' },
        customerOrgId
      );
      if (!voidR?.ok) {
        setSaving(false);
        showMessage(true, voidR?.message || 'Could not void the previous agreement', 'error', 6000);
        return;
      }
    }

    const r = await createAgreement(body);
    setSaving(false);
    if (r?.ok) {
      showMessage(
        true,
        editId ? 'Revised agreement created and invite sent' : 'Agreement created and invite sent',
        'success',
        4000
      );
      if (r.data?.agreementUrl) {
        window.open(r.data.agreementUrl, '_blank', 'noopener');
      }
      history.push(
        customerOrgId
          ? `/admin/agreements?org=${encodeURIComponent(customerOrgId)}`
          : '/admin/agreements'
      );
    } else {
      showMessage(true, friendlyErrorMessage(r), 'error', 6000);
    }
  };

  const countryOptions = (countries || []).map((c) => ({ value: c.id, label: c.name }));
  const contractingEntityDisplay = useMemo(
    () => formatCyfContractingEntity(contractingPreview),
    [contractingPreview],
  );
  const showContractingPreview =
    Boolean(form.customerRegisteredCountry || effectiveBillingCountryId);
  const showPickerEmptyAlert =
    !productsLoading && (!priceOptions || priceOptions.length === 0);

  if (editId && editLoading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (editId && editLoadError) {
    return (
      <div className="p-6 max-w-lg space-y-3">
        <p>{editLoadError}</p>
        <Button onClick={() => history.push('/admin/agreements')}>Back to agreements</Button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Breadcrumb
        items={[
          { label: 'Agreements', link: '/admin/agreements' },
          { label: editId ? 'Edit agreement' : 'New agreement' },
        ]}
      />
      {editId && (
        <Alert className="mb-4">
          <AlertDescription className="text-xs">
            Saving will void the current unsigned agreement and create a revised order form with a
            new invite link.
          </AlertDescription>
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>Customer</CardTitle>
            <Tabs value={customerMode} onValueChange={editId ? () => {} : setCustomerMode}>
              <TabsList>
                <TabsTrigger value="link">Link existing</TabsTrigger>
                <TabsTrigger value="create">Create new</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {customerMode === 'link' ? (
              <div className="md:col-span-2 space-y-3">
                <div>
                  <Label>Existing organisation *</Label>
                  <BrandSelect
                    classNamePrefix="cyf-select"
                    options={orgOptions}
                    value={orgValue}
                    isClearable
                    isSearchable
                    placeholder={
                      !adminOrgs || !adminOrgs.length
                        ? 'Loading organisations...'
                        : 'Search organisations'
                    }
                    onChange={onPickOrg}
                    styles={adminToolbarSelect}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                  {orgValue && (
                    <Alert className="mt-2">
                      <AlertDescription className="flex items-start gap-2 text-xs">
                        <Info size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                        <span>
                          <strong>{orgValue.label}</strong>
                          {linkedOrg?.country ? ` — ${linkedOrg.country}` : ''}
                          {linkedOrg?.currency
                            ? ` (org currency: ${formatOrganisationCurrency(linkedOrg.currency, currencies)})`
                            : ''}
                          {linkedAccountType ? ` · type: ${linkedAccountType}` : ''}
                          . The new agreement will be linked to this org via{' '}
                          <code>customerOrganisationId</code> instead of creating a new one.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {orgValue && (
                  <div className="flex items-start gap-2 rounded-md border border-border p-2">
                    <Checkbox
                      id="replace-sub"
                      checked={replaceExistingSubscription}
                      onCheckedChange={(v) => setReplaceExistingSubscription(!!v)}
                    />
                    <div className="text-xs">
                      <Label htmlFor="replace-sub" className="cursor-pointer">
                        Replace existing subscription (renewal)
                      </Label>
                      <p className="text-muted-foreground">
                        Cancels the org's current Stripe subscription and archives sibling
                        contracts when this agreement is provisioned. Leave off for add-on
                        contracts that should coexist (e.g. Reports add-on).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label>Organisation name *</Label>
                  <Input
                    value={form.customerOrganisationName}
                    onChange={(e) => update('customerOrganisationName', e.target.value)}
                    required
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <Label>Place of incorporation *</Label>
              <Select
                value={form.customerRegisteredCountry}
                onChange={(v) => update('customerRegisteredCountry', v)}
                options={countryOptions}
                placeholder="Select country"
                searchable
                clearable
              />
            </div>

            <div className="md:col-span-2 flex items-start gap-2 rounded-md border border-border p-2">
              <Checkbox
                id="billing-country-differs"
                checked={billingCountryDiffers}
                onCheckedChange={(v) => {
                  setBillingCountryDiffers(!!v);
                  if (!v) update('customerBillingCountry', '');
                }}
              />
              <div className="text-xs">
                <Label htmlFor="billing-country-differs" className="cursor-pointer">
                  Billing country differs from incorporation
                </Label>
                <p className="text-muted-foreground">
                  When unchecked, billing country matches incorporation. If they differ,
                  incorporation governs which CYF entity contracts.
                </p>
              </div>
            </div>

            {billingCountryDiffers && (
              <div className="md:col-span-2">
                <Label>Billing country *</Label>
                <Select
                  value={form.customerBillingCountry}
                  onChange={(v) => update('customerBillingCountry', v)}
                  options={countryOptions}
                  placeholder="Select billing country"
                  searchable
                  clearable
                />
              </div>
            )}

            <div>
              <Label>Billing currency *</Label>
              <Select
                value={form.billingCurrency}
                onChange={onBillingCurrencyChange}
                options={BILLING_CURRENCY_OPTIONS}
                placeholder="Select billing currency"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Invoice currency for this agreement — independent of the linked org&apos;s stored
                currency. Stripe catalog loads for{' '}
                <code>{contractIsoCurrency || '—'}</code>.
              </p>
            </div>

            <div>
              <Label>Type *</Label>
              <Select
                value={form.type}
                onChange={(v) => update('type', v)}
                options={['Ambassador', 'White Label', 'Consultant', 'Direct']}
                placeholder="Type"
                disabled={typeIsLocked}
              />
              {typeIsLocked && (
                <p className="text-xs text-muted-foreground mt-1">
                  Locked to the linked organisation's type ({linkedAccountType}).
                </p>
              )}
            </div>
            <div>
              <Label>Signer first name *</Label>
              <Input value={form.customerFirstName} onChange={(e) => update('customerFirstName', e.target.value)} required />
            </div>
            <div>
              <Label>Signer last name *</Label>
              <Input value={form.customerLastName} onChange={(e) => update('customerLastName', e.target.value)} required />
            </div>
            <div>
              <Label>Signer email *</Label>
              <Input type="email" value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} required />
            </div>
            {form.type === 'White Label' && (
              <div className="md:col-span-2">
                <Label>White label partner *</Label>
                <BrandSelect
                  classNamePrefix="cyf-select"
                  options={wlParentOptions}
                  value={wlParentOptions.find((o) => o.value === form.whiteLabelId) || null}
                  isClearable
                  isSearchable
                  placeholder={
                    !whiteLabelAdminOrgs || !whiteLabelAdminOrgs.length
                      ? 'Loading white label partners...'
                      : 'Search white label partners'
                  }
                  onChange={(opt) => update('whiteLabelId', opt ? opt.value : '0')}
                  styles={adminToolbarSelect}
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            )}
            {form.type === 'Consultant' && (
              <div className="md:col-span-2">
                {linkedOrgIsConsultantParent ? (
                  <Alert className="mb-3">
                    <AlertDescription className="text-xs">
                      <strong>{linkedOrg?.name}</strong> is the consultant organisation — it does not need a separate
                      parent. End-client billing is configured below.
                    </AlertDescription>
                  </Alert>
                ) : customerMode === 'create' ? (
                  <p className="text-xs text-muted-foreground mb-3">
                    A new consultant org will reference itself as parent when the agreement is accepted.
                  </p>
                ) : (
                  <>
                    <Label>Consultant parent *</Label>
                    <BrandSelect
                      classNamePrefix="cyf-select"
                      options={consultantParentOptions}
                      value={consultantParentOptions.find((o) => o.value === form.consultantId) || null}
                      isClearable
                      isSearchable
                      placeholder={
                        !consultantParentOptions.length
                          ? 'No consultant parent orgs found'
                          : 'Search consultant parent orgs'
                      }
                      onChange={(opt) => update('consultantId', opt ? opt.value : 'NONE')}
                      styles={adminToolbarSelect}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Only needed when the customer org above is not the consultant itself (unusual).
                    </p>
                  </>
                )}
              </div>
            )}
            {(form.type === 'Direct' || form.type === 'Ambassador') && (
              <div className="md:col-span-2 text-xs text-muted-foreground">
                Direct / Ambassador agreements stay on Compare Your Footprint
                (<code>whiteLabelId = '0'</code>, <code>consultantId = 'NONE'</code>) — no parent picker needed.
              </div>
            )}
          </CardContent>
        </Card>

        {form.type === 'Consultant' && (
          <Card>
            <CardHeader>
              <CardTitle>End client billing (default)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription className="text-xs">
                  This is how the consultant&apos;s <strong>end clients</strong> are billed — separate from the
                  platform licence line items above (what the consultant pays CYF).
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Default model for end clients</Label>
                  <Select
                    value={form.endClientDefaultPlanType}
                    onValueChange={(v) => update('endClientDefaultPlanType', v)}
                    options={[
                      { value: 'per_report', label: 'Per report (legacy — most consultants)' },
                      { value: 'client_subscription', label: 'Client subscription (per client plan)' },
                    ]}
                  />
                </div>
                {form.endClientDefaultPlanType === 'per_report' && (
                  <div>
                    <Label>Default per-report fee ({form.billingCurrency}) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.perReportFeeVal}
                      onChange={(e) => update('perReportFeeVal', e.target.value)}
                      placeholder="e.g. 45"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Stored on the master contract. Used when onboarding clients unless overridden per client. Client
                      expiry dates are set by CYF admin on each org after onboarding.
                    </p>
                  </div>
                )}
                {form.endClientDefaultPlanType === 'client_subscription' && (
                  <p className="text-sm text-muted-foreground md:col-span-2">
                    Each end client gets their own subscription plan when added from Client Portfolio — no global
                    per-report fee.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showContractingPreview && (
          <Card>
            <CardHeader>
              <CardTitle>CYF contracting party</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {contractingLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resolving contracting entity…
                </div>
              )}
              {!contractingLoading && contractingPreviewError && (
                <Alert>
                  <AlertDescription className="text-xs">{contractingPreviewError}</AlertDescription>
                </Alert>
              )}
              {!contractingLoading && contractingEntityDisplay && (
                <div className="space-y-1">
                  <p className="font-semibold">{contractingEntityDisplay.title}</p>
                  {contractingEntityDisplay.lines.map((line) => (
                    <p key={line} className="text-muted-foreground">
                      {line}
                    </p>
                  ))}
                  {contractingEntityDisplay.isDefault && (
                    <p className="text-xs text-muted-foreground italic">
                      Default jurisdiction (no specific row matched).
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Line items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {showPickerEmptyAlert && (
              <Alert>
                <AlertDescription className="flex items-start gap-2 text-xs">
                  <Info size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <span>
                    No Stripe products were returned for currency{' '}
                    <code>{contractIsoCurrency || '—'}</code>
                    {form.billingCurrency ? (
                      <>
                        {' '}
                        (billing: <code>{form.billingCurrency}</code>)
                      </>
                    ) : null}
                    . Either the
                    flag is off, the catalog is empty, or no Prices match this currency.
                    Subscription line items still need a <code>stripePriceId</code> — check
                    Stripe and try again.
                  </span>
                </AlertDescription>
              </Alert>
            )}
            {form.lineItems.map((row, i) => {
              const selectedPrice =
                row.stripePriceId
                  ? priceOptions.find((o) => o.price?.id === row.stripePriceId) || null
                  : null;
              const isSub = row.kind === 'subscription';
              const tiered = isSub && selectedPrice && isTieredPrice(selectedPrice.price);
              const billingUnit = selectedPrice
                ? resolveBillingUnit(selectedPrice.price, selectedPrice.product)
                : { unit: null, label: null };
              const matchedTier =
                tiered && row.bandingQuantity
                  ? findMatchingTier(selectedPrice.price.tiers, row.bandingQuantity)
                  : null;
              return (
                <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end border border-border p-2 rounded-md">
                  <div>
                    <Label className="text-xs">Kind</Label>
                    <Select
                      value={row.kind}
                      onChange={(v) => setLine(i, 'kind', v)}
                      options={['subscription', 'one_off']}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Billing (sub)</Label>
                    <Input
                      value={row.billingFrequency || ''}
                      onChange={(e) => setLine(i, 'billingFrequency', e.target.value)}
                      placeholder="annual"
                      disabled={!isSub}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Unit ({stripeCurrencyToSymbol(row.currency || contractIsoCurrency) || 'major'})
                    </Label>
                    <Input
                      type="number"
                      value={tiered ? '' : row.unitAmount}
                      onChange={(e) => setLine(i, 'unitAmount', e.target.value)}
                      disabled={tiered}
                      placeholder={tiered ? 'snapshotted from matched tier' : '0.00'}
                    />
                    {tiered && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Tier-banded — server stamps the matched tier's flat amount.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      value={row.quantity}
                      onChange={(e) => setLine(i, 'quantity', e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Description</Label>
                    <Input value={row.description} onChange={(e) => setLine(i, 'description', e.target.value)} />
                  </div>

                  {isSub && (
                    <div className="md:col-span-6">
                      <Label className="text-xs">Stripe product / price *</Label>
                      <BrandSelect
                        classNamePrefix="cyf-select"
                        options={priceOptions}
                        value={selectedPrice}
                        isClearable
                        isSearchable
                        isLoading={!!productsLoading}
                        placeholder={
                          productsLoading
                            ? 'Loading Stripe products...'
                            : priceOptions.length
                              ? 'Search Stripe product / price'
                              : 'No products available for this currency'
                        }
                        onChange={(opt) => onPickPrice(i, opt)}
                        styles={adminToolbarSelect}
                        menuPosition="fixed"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                      {row.stripePriceId && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          <code>{row.stripeProductId}</code> · <code>{row.stripePriceId}</code> ·{' '}
                          <code>{row.currency}</code>
                          {tiered && ' · tiered'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Banding quantity + tiers preview (subscription tiered Prices only) */}
                  {isSub && tiered && (
                    <div className="md:col-span-6 border border-dashed border-border rounded-md p-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <Tag size={14} strokeWidth={1.5} className="shrink-0" />
                        <Label className="text-xs flex-1">
                          {billingUnit.label || 'Banding quantity'} *
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          className="max-w-[260px]"
                          value={row.bandingQuantity ?? ''}
                          onChange={(e) => setLine(i, 'bandingQuantity', e.target.value)}
                          placeholder={
                            billingUnit.unit === 'revenue'
                              ? 'e.g. 8000000'
                              : billingUnit.unit === 'fte'
                                ? 'e.g. 50'
                                : 'positive whole number'
                          }
                        />
                      </div>
                      {Array.isArray(selectedPrice?.price?.tiers) && (
                        <div className="overflow-x-auto">
                          <table className="text-xs w-full border-collapse">
                            <thead>
                              <tr className="text-muted-foreground">
                                <th className="text-left p-1 font-normal">Up to</th>
                                <th className="text-right p-1 font-normal">
                                  Flat ({stripeCurrencyToSymbol(row.currency)})
                                </th>
                                <th className="text-right p-1 font-normal">
                                  Per unit ({stripeCurrencyToSymbol(row.currency)})
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedPrice.price.tiers.map((t, ti) => {
                                const isMatch = matchedTier?.index === ti;
                                const upTo =
                                  t.up_to === 'inf' || t.up_to === null
                                    ? '∞'
                                    : Number(t.up_to).toLocaleString();
                                // Picker tiers come in PENCE — divide for display.
                                const flat = formatMajorMoney(
                                  row.currency,
                                  typeof t.flat_amount === 'number' ? t.flat_amount / 100 : null,
                                  () => ''
                                );
                                const perUnit = formatMajorMoney(
                                  row.currency,
                                  typeof t.unit_amount === 'number' ? t.unit_amount / 100 : null,
                                  () => ''
                                );
                                return (
                                  <tr
                                    key={ti}
                                    className={
                                      isMatch
                                        ? 'bg-primary/10 font-semibold'
                                        : ''
                                    }
                                  >
                                    <td className="p-1">
                                      {isMatch ? '→ ' : ''}
                                      {upTo}
                                    </td>
                                    <td className="p-1 text-right">{flat}</td>
                                    <td className="p-1 text-right">{perUnit}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {matchedTier && (
                        <p className="text-[11px] text-muted-foreground">
                          Customer falls in <strong>tier {matchedTier.index + 1}</strong>
                          {typeof matchedTier.flatAmount === 'number' && (
                            <>
                              {' '}— flat{' '}
                              <strong>
                                {formatMajorMoney(
                                  row.currency,
                                  matchedTier.flatAmount / 100,
                                  stripeCurrencyToSymbol
                                )}
                              </strong>
                            </>
                          )}
                          .
                        </p>
                      )}
                    </div>
                  )}

                  {/* Discount editor (subscription only) */}
                  {isSub && (
                    <div className="md:col-span-6">
                      {row.discount ? (
                        <div className="border border-dashed border-border rounded-md p-2 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-xs flex items-center gap-1">
                              <Tag size={12} strokeWidth={1.5} /> Discount
                            </Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setLineDiscount(i, null)}
                            >
                              Remove
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <div>
                              <Label className="text-xs">Type</Label>
                              <Select
                                value={row.discount.type || 'percent'}
                                onChange={(v) => setLineDiscount(i, { type: v })}
                                options={['percent', 'fixed']}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">
                                Value{' '}
                                {row.discount.type === 'percent'
                                  ? '(%, 0–100)'
                                  : '(major units)'}
                              </Label>
                              <Input
                                type="number"
                                value={row.discount.value ?? ''}
                                onChange={(e) => setLineDiscount(i, { value: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Duration</Label>
                              <Select
                                value={row.discount.duration || 'forever'}
                                onChange={(v) => setLineDiscount(i, { duration: v })}
                                options={['forever', 'once', 'repeating']}
                              />
                            </div>
                            {row.discount.duration === 'repeating' && (
                              <div>
                                <Label className="text-xs">Months *</Label>
                                <Input
                                  type="number"
                                  value={row.discount.durationInMonths ?? ''}
                                  onChange={(e) =>
                                    setLineDiscount(i, { durationInMonths: e.target.value })
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setLineDiscount(i, {
                              type: 'percent',
                              value: 10,
                              duration: 'forever',
                            })
                          }
                        >
                          <Tag size={14} strokeWidth={1.5} className="mr-1" />
                          Add discount
                        </Button>
                      )}
                    </div>
                  )}

                  <div className="md:col-span-6 flex items-center justify-end">
                    {form.lineItems.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <Button type="button" variant="outline" onClick={addLine}>
              <Plus size={16} strokeWidth={1.5} className="mr-1" />
              Add line
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Term & indexation</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Term type</Label>
              <Select value={form.term.type} onChange={(v) => updateTerm('type', v)} options={['committed', 'rolling']} />
            </div>
            <div>
              <Label>Length (months)</Label>
              <Input type="number" value={form.term.lengthMonths} onChange={(e) => updateTerm('lengthMonths', e.target.value)} />
            </div>
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.term.startDate} onChange={(e) => updateTerm('startDate', e.target.value)} />
            </div>
            <div>
              <Label>Notice (days)</Label>
              <Input type="number" value={form.term.noticePeriodDays} onChange={(e) => updateTerm('noticePeriodDays', e.target.value)} />
            </div>
            <div>
              <Label>Indexation kind</Label>
              <Select
                value={form.indexationPolicy.kind}
                onChange={(v) => updateIdx('kind', v)}
                options={['none', 'fixed_percent', 'cpi_with_floor']}
              />
            </div>
            {form.indexationPolicy.kind === 'fixed_percent' && (
              <>
                <div>
                  <Label>Annual increase %</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.indexationPolicy.floorPercent}
                    onChange={(e) => updateIdx('floorPercent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fixed uplift applied each indexation anniversary (sent as{' '}
                    <code>floorPercent</code> on the contract).
                  </p>
                </div>
                <div>
                  <Label>Notice days</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.noticeDays}
                    onChange={(e) => updateIdx('noticeDays', e.target.value)}
                  />
                </div>
              </>
            )}
            {form.indexationPolicy.kind === 'cpi_with_floor' && (
              <>
                <div>
                  <Label>Floor %</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.floorPercent}
                    onChange={(e) => updateIdx('floorPercent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Minimum uplift when CPI is lower than this floor.
                  </p>
                </div>
                <div>
                  <Label>CPI source</Label>
                  <Select
                    value={form.indexationPolicy.cpiSource}
                    onChange={(v) => updateIdx('cpiSource', v)}
                    options={['UK_CPIH', 'UK_CPI', 'UK_RPI']}
                    placeholder="CPI source"
                  />
                </div>
                <div>
                  <Label>Notice days</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.noticeDays}
                    onChange={(e) => updateIdx('noticeDays', e.target.value)}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pay mode</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={form.payMode}
              onChange={(v) => update('payMode', v)}
              options={['invoice', 'checkout', 'both']}
            />
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" loading={saving} disabled={saving}>
            {editId ? 'Save revised agreement & send invite' : 'Create & send invite'}
          </Button>
          <Button type="button" variant="outline" onClick={() => history.push('/admin/agreements')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

const mapState = (s) => ({
  countries: s?.organisation?.countries,
  agreementsList: s?.agreements?.list || [],
  listOrganisationId: s?.agreements?.listOrganisationId,
  adminOrgs: s?.admin?.organisations,
  whiteLabelAdminOrgs: s?.admin?.whiteLabelAdminOrgs,
  productsByCurrency: s?.agreements?.products?.byCurrency,
  productsLoading: s?.agreements?.loading?.products,
  currencies:
    s?.admin?.adminPortal?.currencies?.length > 0
      ? s.admin.adminPortal.currencies
      : s?.organisation?.currencies,
});

const mapDispatch = (d) => ({
  createAgreement: (b) => d(AGREEMENTS.createAgreement(b)),
  voidAgreement: (id, body, oid) => d(AGREEMENTS.voidAgreement(id, body, oid)),
  getAgreementContract: (id, oid) => d(AGREEMENTS.getAgreementContract(id, oid)),
  getStripeProducts: (params) => d(AGREEMENTS.getStripeProducts(params)),
  getAdminOrganisations: () => d(ADMINACTIONS.getAdminOrganisations()),
  getWLAdminOrgs: () => d(ORGANISATIONACTIONS.getWLAdminOrgs()),
  loadCurrencies: () => d(getAllItemsFromDB('currencies')),
  listCountries: () => d(ORGANISATIONACTIONS.listCountries()),
  getContractingEntity: (params) => d(LEGAL.getContractingEntity(params)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(NewAgreement);
